import { forwardRef } from "react";
import { Server } from "lucide-react";

function formatDate(iso) {
  if (!iso) return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

/* Industry-standard Certificate of Completion (1000x707) — inline styles for reliable PNG export. */
const Certificate = forwardRef(function Certificate({ studentName, title, program, completedAt, certificateId, verificationId }, ref) {
  const programName = program || title || "HMG Linux Hands-On Lab Program";
  const Corner = ({ pos }) => (
    <svg width="66" height="66" viewBox="0 0 66 66" style={{ position: "absolute", ...pos }} fill="none">
      <path d="M2 64 L2 22 Q2 2 22 2 L64 2" stroke="#c9a227" strokeWidth="2.5" fill="none" />
      <path d="M12 64 L12 30 Q12 12 30 12 L64 12" stroke="#c9a227" strokeWidth="1" fill="none" opacity="0.7" />
    </svg>
  );
  return (
    <div
      ref={ref}
      style={{
        width: 1000,
        height: 707,
        background: "linear-gradient(135deg, #fffdf7 0%, #fbf6ea 100%)",
        position: "relative",
        fontFamily: 'Georgia, "Times New Roman", serif',
        color: "#1a1f36",
        boxSizing: "border-box",
        padding: 26,
      }}
    >
      {/* Double frame */}
      <div style={{ position: "absolute", inset: 26, border: "2px solid #0b1b3a" }} />
      <div style={{ position: "absolute", inset: 34, border: "1px solid #c9a227" }} />

      {/* Ornamental corners */}
      <Corner pos={{ top: 30, left: 30, transform: "rotate(0deg)" }} />
      <Corner pos={{ top: 30, right: 30, transform: "rotate(90deg)" }} />
      <Corner pos={{ bottom: 30, right: 30, transform: "rotate(180deg)" }} />
      <Corner pos={{ bottom: 30, left: 30, transform: "rotate(270deg)" }} />

      <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "44px 78px 34px" }}>
        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 8, background: "#0b1b3a", display: "grid", placeItems: "center" }}>
            <Server color="#c9a227" size={22} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontWeight: 900, fontSize: 20, letterSpacing: 1.5, lineHeight: 1, color: "#0b1b3a" }}>HMG GLOBAL CONSULTS</div>
            <div style={{ fontSize: 10, color: "#8a7220", letterSpacing: 4, textTransform: "uppercase", marginTop: 4, fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>Linux Hands-On Lab · USA</div>
          </div>
        </div>

        <div style={{ marginTop: 26, fontSize: 42, letterSpacing: 6, color: "#0b1b3a", fontWeight: 800, textTransform: "uppercase" }}>
          Certificate
        </div>
        <div style={{ fontSize: 15, letterSpacing: 8, color: "#c9a227", textTransform: "uppercase", marginTop: 2, fontFamily: '"Helvetica Neue", Arial, sans-serif', fontWeight: 600 }}>
          of Completion
        </div>

        <div style={{ marginTop: 22, fontSize: 14, color: "#5b6478", fontStyle: "italic" }}>This certificate is proudly presented to</div>

        <div style={{ fontWeight: 800, fontSize: 52, lineHeight: 1.05, marginTop: 6, color: "#0b1b3a" }}>
          {studentName || "Student"}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "12px 0 16px" }}>
          <span style={{ width: 90, height: 1, background: "#c9a227" }} />
          <span style={{ width: 6, height: 6, background: "#c9a227", transform: "rotate(45deg)" }} />
          <span style={{ width: 90, height: 1, background: "#c9a227" }} />
        </div>

        <div style={{ fontSize: 15.5, color: "#3a4257", maxWidth: 700, lineHeight: 1.65, fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>
          for successfully completing all required hands-on laboratories of the
          <span style={{ fontWeight: 700, color: "#0b1b3a" }}> {programName}</span>, demonstrating practical proficiency in Linux command-line operations and system administration.
        </div>

        {/* Footer: signatures + seal */}
        <div style={{ marginTop: "auto", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div style={{ textAlign: "center", minWidth: 210 }}>
            <div style={{ fontWeight: 700, fontSize: 17, borderBottom: "1.5px solid #0b1b3a", paddingBottom: 4 }}>
              {formatDate(completedAt)}
            </div>
            <div style={{ fontSize: 10, color: "#5b6478", marginTop: 6, letterSpacing: 2, textTransform: "uppercase", fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>Date of Issue</div>
          </div>

          {/* Gold seal + ribbon */}
          <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{
              width: 92, height: 92, borderRadius: "50%",
              background: "radial-gradient(circle at 35% 30%, #f4d675, #c9a227 60%, #a9861d)",
              display: "grid", placeItems: "center", boxShadow: "0 4px 14px rgba(169,134,29,0.5)",
              border: "3px solid #fffdf7",
            }}>
              <div style={{ width: 68, height: 68, borderRadius: "50%", border: "1.5px dashed rgba(11,27,58,0.55)", display: "grid", placeItems: "center", textAlign: "center" }}>
                <div>
                  <div style={{ fontSize: 26, lineHeight: 1 }}>🐧</div>
                  <div style={{ fontSize: 7, letterSpacing: 1.5, color: "#0b1b3a", fontWeight: 800, marginTop: 1, fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>CERTIFIED</div>
                </div>
              </div>
            </div>
            {/* ribbon tails */}
            <div style={{ display: "flex", gap: 4, marginTop: -6 }}>
              <div style={{ width: 12, height: 22, background: "#c9a227", clipPath: "polygon(0 0,100% 0,100% 100%,50% 78%,0 100%)" }} />
              <div style={{ width: 12, height: 22, background: "#a9861d", clipPath: "polygon(0 0,100% 0,100% 100%,50% 78%,0 100%)" }} />
            </div>
            <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 6, fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>{certificateId}</div>
            {verificationId ? <div style={{ fontSize: 8, color: "#94a3b8", marginTop: 1, fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>Verify: {verificationId}</div> : null}
          </div>

          <div style={{ textAlign: "center", minWidth: 210 }}>
            <div style={{ fontWeight: 700, fontSize: 17, borderBottom: "1.5px solid #0b1b3a", paddingBottom: 4, fontStyle: "italic" }}>
              HMG Global Consults
            </div>
            <div style={{ fontSize: 10, color: "#5b6478", marginTop: 6, letterSpacing: 2, textTransform: "uppercase", fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>Authorized Issuer</div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Certificate;
